import * as React from 'react';

import { enumUnit, Unit, Weight } from '@rematter/frontend-lib-api-client';
import {
  enumLoadWeightType,
  LoadFormLineItem,
} from '@rematter/modules/trade/types/load';
import {
  convertFromPounds,
  convertToPounds,
  shortenUnit,
} from '@rematter/ui-web/lib';

import { PricingUnitFieldCellProps } from '../types';

import { UnitFieldCell } from '~/shared/components/line-items';

export const PricingUnitFieldCell: React.FC<PricingUnitFieldCellProps> = ({
  lineItem,
  disabled = false,
  onUpdateLineItem,
}) => {
  const handlePricingUnitChange = React.useCallback(
    (value?: Unit) => {
      const newPricingUnit = value ?? enumUnit.Pounds;
      const oldPricingUnit = lineItem.pricingUnit ?? enumUnit.Pounds;

      const updates: Partial<LoadFormLineItem> = {
        pricingUnit: newPricingUnit,
      };

      if (oldPricingUnit !== newPricingUnit) {
        updates.weights = {};

        const convertWeight = (weight: Weight) => {
          const oldValue = weight.value;
          const oldUnit = weight.unit as Unit;

          const pounds = convertToPounds(oldValue, oldUnit);
          const newValue = convertFromPounds(pounds, newPricingUnit);

          return {
            ...weight,
            value: newValue,
            unit: newPricingUnit,
          };
        };

        if (lineItem.weights?.[enumLoadWeightType.Gross]) {
          updates.weights[enumLoadWeightType.Gross] = convertWeight(
            lineItem.weights[enumLoadWeightType.Gross],
          );
        }

        if (lineItem.weights?.[enumLoadWeightType.Tare]) {
          updates.weights[enumLoadWeightType.Tare] = convertWeight(
            lineItem.weights[enumLoadWeightType.Tare],
          );
        }

        if (lineItem.weights?.[enumLoadWeightType.Net]) {
          updates.weights[enumLoadWeightType.Net] = convertWeight(
            lineItem.weights[enumLoadWeightType.Net],
          );
        }

        if (lineItem.weights?.[enumLoadWeightType.QuantityOwed]) {
          updates.weights[enumLoadWeightType.QuantityOwed] = convertWeight(
            lineItem.weights[enumLoadWeightType.QuantityOwed],
          );
        }
      }

      if (lineItem.unitPrice && lineItem.weights?.[enumLoadWeightType.Net]) {
        const netWeight =
          updates.weights?.[enumLoadWeightType.Net] ??
          lineItem.weights[enumLoadWeightType.Net];
        const netWeightValue = netWeight.value;
        const unitPriceAmount = lineItem.unitPrice.amount ?? 0;
        const currencyCode = lineItem.unitPrice.currencyCode;

        const estimatedAmount = unitPriceAmount * netWeightValue;

        updates.estimatedPrice = {
          amount: estimatedAmount,
          currencyCode,
          __typename: 'Money' as const,
        };
      }

      onUpdateLineItem(lineItem.id, updates);
    },
    [lineItem, onUpdateLineItem],
  );

  return (
    <UnitFieldCell
      value={(lineItem.pricingUnit as Unit) || enumUnit.Pounds}
      onChange={handlePricingUnitChange}
      disabled={disabled}
      formatLabel={shortenUnit}
      className="@PricingUnitFieldCell"
    />
  );
};
